/**
 * Central icon module — every app icon is re-exported here from Iconsax with
 * the `Linear` variant as the default. Import icons from '@/components/icons',
 * never from 'iconsax-reactjs' directly, so the variant (and any future
 * icon-set change) stays a one-file decision.
 *
 * Linear (thin outline) is the quiet default for supporting/functional icons
 * (menus, toolbars, inline metadata, form fields). Where the icon is itself a
 * visual anchor or signals an active/semantic state (stat tiles, status
 * badges, active nav, decorative hero marks), pass `variant="Bulk"` at the
 * call site to opt into the two-tone fill.
 *
 * A few glyphs have no Iconsax equivalent (bare checkmark/×, drag grips,
 * spinner, list bullets…) and are drawn inline below, sized and colored
 * with the same `size` / `color` contract as Iconsax components. They are
 * inherently stroke-drawn and ignore `variant`.
 */
import {
    Add as IxAdd,
    AddSquare as IxAddSquare,
    ArchiveTick as IxArchiveTick,
    ArrowDown as IxArrowDown,
    ArrowDown2 as IxArrowDown2,
    ArrowLeft as IxArrowLeft,
    ArrowLeft2 as IxArrowLeft2,
    ArrowRight as IxArrowRight,
    ArrowRight2 as IxArrowRight2,
    ArrowRotateLeft as IxArrowRotateLeft,
    ArrowRotateRight as IxArrowRotateRight,
    ArrowSwapHorizontal as IxArrowSwapHorizontal,
    ArrowUp as IxArrowUp,
    ArrowUp2 as IxArrowUp2,
    Book as IxBook,
    Book1 as IxBook1,
    Brush2 as IxBrush2,
    Calculator as IxCalculator,
    Calendar as IxCalendar,
    Calendar1 as IxCalendar1,
    CalendarTick as IxCalendarTick,
    Call as IxCall,
    Card as IxCard,
    Category as IxCategory,
    Chart as IxChart,
    Chart21 as IxChart21,
    ChemicalGlass as IxChemicalGlass,
    ClipboardImport as IxClipboardImport,
    ClipboardText as IxClipboardText,
    ClipboardTick as IxClipboardTick,
    Clock as IxClock,
    CloseCircle as IxCloseCircle,
    Colorfilter as IxColorfilter,
    ColorSwatch as IxColorSwatch,
    Convertshape as IxConvertshape,
    Convertshape2 as IxConvertshape2,
    Copy as IxCopy,
    Cpu as IxCpu,
    CpuSetting as IxCpuSetting,
    Danger as IxDanger,
    Designtools as IxDesigntools,
    Diagram as IxDiagram,
    DirectInbox as IxDirectInbox,
    Dislike as IxDislike,
    DocumentText as IxDocumentText,
    Edit as IxEdit,
    Edit2 as IxEdit2,
    Element3 as IxElement3,
    Element4 as IxElement4,
    Export as IxExport,
    ExportCurve as IxExportCurve,
    ExportSquare as IxExportSquare,
    Eye as IxEye,
    EyeSlash as IxEyeSlash,
    Filter as IxFilter,
    Flag as IxFlag,
    FolderOpen as IxFolderOpen,
    Gps as IxGps,
    Graph as IxGraph,
    Grid1 as IxGrid1,
    Grid2 as IxGrid2,
    HamburgerMenu as IxHamburgerMenu,
    Headphone as IxHeadphone,
    Image as IxImage,
    ImportCurve as IxImportCurve,
    InfoCircle as IxInfoCircle,
    Instagram as IxInstagram,
    Keyboard as IxKeyboard,
    LampOn as IxLampOn,
    Lifebuoy as IxLifebuoy,
    Like1 as IxLike1,
    Link21 as IxLink21,
    Location as IxLocation,
    Lock as IxLock,
    Lock1 as IxLock1,
    Logout as IxLogout,
    Math as IxMath,
    Maximize2 as IxMaximize2,
    Maximize4 as IxMaximize4,
    Message as IxMessage,
    MessageQuestion as IxMessageQuestion,
    Messages2 as IxMessages2,
    MessageText1 as IxMessageText1,
    Microphone2 as IxMicrophone2,
    Minus as IxMinus,
    Monitor as IxMonitor,
    Moon as IxMoon,
    Note1 as IxNote1,
    NoteAdd as IxNoteAdd,
    Notepad2 as IxNotepad2,
    Paperclip2 as IxPaperclip2,
    Pause as IxPause,
    People as IxPeople,
    Play as IxPlay,
    PlayCircle as IxPlayCircle,
    Printer as IxPrinter,
    Profile as IxProfile,
    Profile2User as IxProfile2User,
    QuoteUp as IxQuoteUp,
    Radar2 as IxRadar2,
    RecordCircle as IxRecordCircle,
    Refresh as IxRefresh,
    RowVertical as IxRowVertical,
    SaveAdd as IxSaveAdd,
    Scissor as IxScissor,
    SearchNormal1 as IxSearchNormal1,
    SearchStatus as IxSearchStatus,
    SearchZoomIn as IxSearchZoomIn,
    SearchZoomOut as IxSearchZoomOut,
    Send2 as IxSend2,
    Setting2 as IxSetting2,
    Setting4 as IxSetting4,
    Share as IxShare,
    ShieldTick as IxShieldTick,
    SidebarBottom as IxSidebarBottom,
    SidebarLeft as IxSidebarLeft,
    SidebarRight as IxSidebarRight,
    SidebarTop as IxSidebarTop,
    Slash as IxSlash,
    Sms as IxSms,
    Sort as IxSort,
    Speedometer as IxSpeedometer,
    Star1 as IxStar1,
    Stop as IxStop,
    Sun1 as IxSun1,
    Tag as IxTag,
    TaskSquare as IxTaskSquare,
    Text as IxText,
    TextalignCenter as IxTextalignCenter,
    TextalignLeft as IxTextalignLeft,
    TextalignRight as IxTextalignRight,
    TextBold as IxTextBold,
    TextItalic as IxTextItalic,
    TextUnderline as IxTextUnderline,
    TickCircle as IxTickCircle,
    Trash as IxTrash,
    TrendUp as IxTrendUp,
    User as IxUser,
    UserAdd as IxUserAdd,
    UserTick as IxUserTick,
    Warning2 as IxWarning2,
    Youtube as IxYoutube,
} from 'iconsax-reactjs';
import { forwardRef } from 'react';
import type { Icon as IconsaxIcon, IconProps as IconsaxProps } from 'iconsax-reactjs';

/** Props accepted by app icons — Iconsax props; `variant` defaults to Linear. */
export type IconProps = Omit<IconsaxProps, 'ref'>;

/** Allowed icon styles (e.g. 'Linear' default, 'Bulk' for emphasis contexts). */
export type IconVariant = NonNullable<IconProps['variant']>;

/** Component type for icon props/config fields (replaces lucide's `LucideIcon`). */
export type IconComponent = React.ForwardRefExoticComponent<
    IconProps & React.RefAttributes<SVGSVGElement>
>;

function icon(Base: IconsaxIcon, name: string): IconComponent {
    const Component = forwardRef<SVGSVGElement, IconProps>((props, ref) => (
        <Base variant="Linear" ref={ref} {...props} />
    ));
    Component.displayName = name;
    return Component;
}

/**
 * Custom glyphs with no Iconsax equivalent. Stroke-drawn (these shapes are
 * inherently linear) but honoring the same `size`/`color` contract.
 * `color` lands on the svg's CSS color so inner currentColor fills/strokes
 * follow it, and Tailwind classes like `fill-current` keep working.
 */
function custom(name: string, children: React.ReactNode): IconComponent {
    const Component = forwardRef<SVGSVGElement, IconProps>(
        ({ size = 24, color = 'currentColor', style, variant: _variant, ...rest }, ref) => (
            <svg
                ref={ref}
                xmlns="http://www.w3.org/2000/svg"
                width={size}
                height={size}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={color !== 'currentColor' ? { color, ...style } : style}
                {...rest}
            >
                {children}
            </svg>
        ),
    );
    Component.displayName = name;
    return Component;
}

// -- Iconsax icons (Linear default) -----------------------------------------
export const Add = icon(IxAdd, 'Add');
export const AddSquare = icon(IxAddSquare, 'AddSquare');
export const ArchiveTick = icon(IxArchiveTick, 'ArchiveTick');
export const ArrowDown = icon(IxArrowDown, 'ArrowDown');
export const ArrowDown2 = icon(IxArrowDown2, 'ArrowDown2');
export const ArrowLeft = icon(IxArrowLeft, 'ArrowLeft');
export const ArrowLeft2 = icon(IxArrowLeft2, 'ArrowLeft2');
export const ArrowRight = icon(IxArrowRight, 'ArrowRight');
export const ArrowRight2 = icon(IxArrowRight2, 'ArrowRight2');
export const ArrowRotateLeft = icon(IxArrowRotateLeft, 'ArrowRotateLeft');
export const ArrowRotateRight = icon(IxArrowRotateRight, 'ArrowRotateRight');
export const ArrowSwapHorizontal = icon(IxArrowSwapHorizontal, 'ArrowSwapHorizontal');
export const ArrowUp = icon(IxArrowUp, 'ArrowUp');
export const ArrowUp2 = icon(IxArrowUp2, 'ArrowUp2');
export const Book = icon(IxBook, 'Book');
export const Book1 = icon(IxBook1, 'Book1');
export const Brush2 = icon(IxBrush2, 'Brush2');
export const Calculator = icon(IxCalculator, 'Calculator');
export const Calendar = icon(IxCalendar, 'Calendar');
export const Calendar1 = icon(IxCalendar1, 'Calendar1');
export const CalendarTick = icon(IxCalendarTick, 'CalendarTick');
export const Call = icon(IxCall, 'Call');
export const Card = icon(IxCard, 'Card');
export const Category = icon(IxCategory, 'Category');
export const Chart = icon(IxChart, 'Chart');
export const Chart21 = icon(IxChart21, 'Chart21');
export const ChemicalGlass = icon(IxChemicalGlass, 'ChemicalGlass');
export const ClipboardImport = icon(IxClipboardImport, 'ClipboardImport');
export const ClipboardText = icon(IxClipboardText, 'ClipboardText');
export const ClipboardTick = icon(IxClipboardTick, 'ClipboardTick');
export const Clock = icon(IxClock, 'Clock');
export const CloseCircle = icon(IxCloseCircle, 'CloseCircle');
export const Colorfilter = icon(IxColorfilter, 'Colorfilter');
export const ColorSwatch = icon(IxColorSwatch, 'ColorSwatch');
export const Convertshape = icon(IxConvertshape, 'Convertshape');
export const Convertshape2 = icon(IxConvertshape2, 'Convertshape2');
export const Copy = icon(IxCopy, 'Copy');
export const Cpu = icon(IxCpu, 'Cpu');
export const CpuSetting = icon(IxCpuSetting, 'CpuSetting');
export const Danger = icon(IxDanger, 'Danger');
export const Designtools = icon(IxDesigntools, 'Designtools');
export const Diagram = icon(IxDiagram, 'Diagram');
export const DirectInbox = icon(IxDirectInbox, 'DirectInbox');
export const Dislike = icon(IxDislike, 'Dislike');
export const DocumentText = icon(IxDocumentText, 'DocumentText');
export const Edit = icon(IxEdit, 'Edit');
export const Edit2 = icon(IxEdit2, 'Edit2');
export const Element3 = icon(IxElement3, 'Element3');
export const Element4 = icon(IxElement4, 'Element4');
export const Export = icon(IxExport, 'Export');
export const ExportCurve = icon(IxExportCurve, 'ExportCurve');
export const ExportSquare = icon(IxExportSquare, 'ExportSquare');
export const Eye = icon(IxEye, 'Eye');
export const EyeSlash = icon(IxEyeSlash, 'EyeSlash');
export const Filter = icon(IxFilter, 'Filter');
export const Flag = icon(IxFlag, 'Flag');
export const FolderOpen = icon(IxFolderOpen, 'FolderOpen');
export const Gps = icon(IxGps, 'Gps');
export const Graph = icon(IxGraph, 'Graph');
export const Grid1 = icon(IxGrid1, 'Grid1');
export const Grid2 = icon(IxGrid2, 'Grid2');
export const HamburgerMenu = icon(IxHamburgerMenu, 'HamburgerMenu');
export const Headphone = icon(IxHeadphone, 'Headphone');
export const Image = icon(IxImage, 'Image');
export const ImportCurve = icon(IxImportCurve, 'ImportCurve');
export const InfoCircle = icon(IxInfoCircle, 'InfoCircle');
export const Instagram = icon(IxInstagram, 'Instagram');
export const Keyboard = icon(IxKeyboard, 'Keyboard');
export const LampOn = icon(IxLampOn, 'LampOn');
export const Lifebuoy = icon(IxLifebuoy, 'Lifebuoy');
export const Like1 = icon(IxLike1, 'Like1');
export const Link21 = icon(IxLink21, 'Link21');
export const Location = icon(IxLocation, 'Location');
export const Lock = icon(IxLock, 'Lock');
export const Lock1 = icon(IxLock1, 'Lock1');
export const Logout = icon(IxLogout, 'Logout');
export const MathIcon = icon(IxMath, 'MathIcon');
export const Maximize2 = icon(IxMaximize2, 'Maximize2');
export const Maximize4 = icon(IxMaximize4, 'Maximize4');
export const Message = icon(IxMessage, 'Message');
export const MessageQuestion = icon(IxMessageQuestion, 'MessageQuestion');
export const Messages2 = icon(IxMessages2, 'Messages2');
export const MessageText1 = icon(IxMessageText1, 'MessageText1');
export const Microphone2 = icon(IxMicrophone2, 'Microphone2');
export const Minus = icon(IxMinus, 'Minus');
export const Monitor = icon(IxMonitor, 'Monitor');
export const Moon = icon(IxMoon, 'Moon');
export const Note1 = icon(IxNote1, 'Note1');
export const NoteAdd = icon(IxNoteAdd, 'NoteAdd');
export const Notepad2 = icon(IxNotepad2, 'Notepad2');
export const Paperclip2 = icon(IxPaperclip2, 'Paperclip2');
export const Pause = icon(IxPause, 'Pause');
export const People = icon(IxPeople, 'People');
export const Play = icon(IxPlay, 'Play');
export const PlayCircle = icon(IxPlayCircle, 'PlayCircle');
export const Printer = icon(IxPrinter, 'Printer');
export const Profile = icon(IxProfile, 'Profile');
export const Profile2User = icon(IxProfile2User, 'Profile2User');
export const QuoteUp = icon(IxQuoteUp, 'QuoteUp');
export const Radar2 = icon(IxRadar2, 'Radar2');
export const RecordCircle = icon(IxRecordCircle, 'RecordCircle');
export const Refresh = icon(IxRefresh, 'Refresh');
export const RowVertical = icon(IxRowVertical, 'RowVertical');
export const SaveAdd = icon(IxSaveAdd, 'SaveAdd');
export const Scissor = icon(IxScissor, 'Scissor');
export const SearchNormal1 = icon(IxSearchNormal1, 'SearchNormal1');
export const SearchStatus = icon(IxSearchStatus, 'SearchStatus');
export const SearchZoomIn = icon(IxSearchZoomIn, 'SearchZoomIn');
export const SearchZoomOut = icon(IxSearchZoomOut, 'SearchZoomOut');
export const Send2 = icon(IxSend2, 'Send2');
export const Setting2 = icon(IxSetting2, 'Setting2');
export const Setting4 = icon(IxSetting4, 'Setting4');
export const Share = icon(IxShare, 'Share');
export const ShieldTick = icon(IxShieldTick, 'ShieldTick');
export const SidebarBottom = icon(IxSidebarBottom, 'SidebarBottom');
export const SidebarLeft = icon(IxSidebarLeft, 'SidebarLeft');
export const SidebarRight = icon(IxSidebarRight, 'SidebarRight');
export const SidebarTop = icon(IxSidebarTop, 'SidebarTop');
export const Slash = icon(IxSlash, 'Slash');
export const Sms = icon(IxSms, 'Sms');
export const Sort = icon(IxSort, 'Sort');
export const Speedometer = icon(IxSpeedometer, 'Speedometer');
export const Star1 = icon(IxStar1, 'Star1');
export const Stop = icon(IxStop, 'Stop');
export const Sun1 = icon(IxSun1, 'Sun1');
export const Tag = icon(IxTag, 'Tag');
export const TaskSquare = icon(IxTaskSquare, 'TaskSquare');
export const Text = icon(IxText, 'Text');
export const TextalignCenter = icon(IxTextalignCenter, 'TextalignCenter');
export const TextalignLeft = icon(IxTextalignLeft, 'TextalignLeft');
export const TextalignRight = icon(IxTextalignRight, 'TextalignRight');
export const TextBold = icon(IxTextBold, 'TextBold');
export const TextItalic = icon(IxTextItalic, 'TextItalic');
export const TextUnderline = icon(IxTextUnderline, 'TextUnderline');
export const TickCircle = icon(IxTickCircle, 'TickCircle');
export const Trash = icon(IxTrash, 'Trash');
export const TrendUp = icon(IxTrendUp, 'TrendUp');
export const User = icon(IxUser, 'User');
export const UserAdd = icon(IxUserAdd, 'UserAdd');
export const UserTick = icon(IxUserTick, 'UserTick');
export const Warning2 = icon(IxWarning2, 'Warning2');
export const Youtube = icon(IxYoutube, 'Youtube');

// -- Custom glyphs (no Iconsax equivalent) ----------------------------------
/** Bare checkmark (Iconsax's `Check` is a bank cheque — do not use it). */
export const Tick = custom('Tick', <path d="M5 12.5l4.5 4.5L19 7" strokeWidth={2.5} />);

/** Bare × for dialog close / chips (CloseCircle is too heavy at small sizes). */
export const Cross = custom(
    'Cross',
    <>
        <path d="M18 6L6 18" />
        <path d="M6 6l12 12" />
    </>,
);

/**
 * Filled rounded square — the universal "stop what is running" mark.
 *
 * Drawn filled rather than stroked because at 14px an outlined square reads as
 * an empty checkbox. Iconsax's `Stop` is a media-player glyph in a circle,
 * which is a different promise.
 */
export const StopSquare = custom(
    'StopSquare',
    <rect x="7" y="7" width="10" height="10" rx="2.5" fill="currentColor" stroke="none" />,
);

/** 3/4 arc — pairs with `animate-spin` (replaces lucide Loader2). */
export const Spinner = custom('Spinner', <path d="M21 12a9 9 0 1 1-6.22-8.56" strokeWidth={2.5} />);

/**
 * Vertical three-dots menu trigger (kebab). Iconsax's `More` is horizontal and
 * `More2` is a triangle, so the app's overflow menus draw this instead.
 */
export const MoreVertical = custom(
    'MoreVertical',
    <>
        <circle cx="12" cy="5" r="2" fill="currentColor" stroke="none" />
        <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
        <circle cx="12" cy="19" r="2" fill="currentColor" stroke="none" />
    </>,
);

/** Plain circle; `fill-current` turns it into a solid dot (radio indicators). */
export const Circle = custom('Circle', <circle cx="12" cy="12" r="9" />);

/** Classic floppy diskette for save buttons (Iconsax's `Save2` is a bookmark). */
export const Floppy = custom(
    'Floppy',
    <>
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
        <path d="M17 21v-8H7v8" />
        <path d="M7 3v5h8" />
    </>,
);

export const GripVertical = custom(
    'GripVertical',
    <>
        {[5, 12, 19].map((y) => (
            <g key={y}>
                <circle cx="9" cy={y} r="1.4" fill="currentColor" stroke="none" />
                <circle cx="15" cy={y} r="1.4" fill="currentColor" stroke="none" />
            </g>
        ))}
    </>,
);

export const GripHorizontal = custom(
    'GripHorizontal',
    <>
        {[5, 12, 19].map((x) => (
            <g key={x}>
                <circle cx={x} cy="9" r="1.4" fill="currentColor" stroke="none" />
                <circle cx={x} cy="15" r="1.4" fill="currentColor" stroke="none" />
            </g>
        ))}
    </>,
);

/** Stacked up/down chevrons (shadcn select trigger). */
export const ChevronsUpDown = custom(
    'ChevronsUpDown',
    <>
        <path d="M7 9l5-5 5 5" />
        <path d="M7 15l5 5 5-5" />
    </>,
);

export const SeparatorHorizontal = custom(
    'SeparatorHorizontal',
    <>
        <path d="M3 12h18" />
        <path d="M8 7l4-4 4 4" />
        <path d="M8 17l4 4 4-4" />
    </>,
);

/** Bullet list (Iconsax has no bullet/numbered list glyphs). */
export const ListBullets = custom(
    'ListBullets',
    <>
        <circle cx="4.5" cy="6" r="1.4" fill="currentColor" stroke="none" />
        <circle cx="4.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
        <circle cx="4.5" cy="18" r="1.4" fill="currentColor" stroke="none" />
        <path d="M9.5 6h11" />
        <path d="M9.5 12h11" />
        <path d="M9.5 18h11" />
    </>,
);

/** Numbered list. */
export const ListNumbers = custom(
    'ListNumbers',
    <>
        <path d="M10 6h11" />
        <path d="M10 12h11" />
        <path d="M10 18h11" />
        <path d="M4 6h1v4" />
        <path d="M4 10h2" />
        <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
    </>,
);

/** Strikethrough (text editor). */
export const TextStrikethrough = custom(
    'TextStrikethrough',
    <>
        <path d="M16 4H9a3 3 0 0 0-2.83 4" />
        <path d="M14 12a4 4 0 0 1 0 8H6" />
        <path d="M4 12h16" />
    </>,
);

const SPARKLE_LARGE =
    'M9.78 6.44L9.2 7.99C8.45 10.02 8.08 11.04 7.34 11.78C6.6 12.52 5.58 12.9 3.55 13.65L2 14.22L3.55 14.8C5.58 15.55 6.6 15.92 7.34 16.66C8.08 17.4 8.45 18.42 9.2 20.45L9.78 22L10.35 20.45C11.1 18.42 11.48 17.4 12.22 16.66C12.96 15.92 13.98 15.55 16.01 14.8L17.56 14.22L16.01 13.65C13.98 12.9 12.96 12.52 12.22 11.78C11.48 11.04 11.1 10.02 10.35 7.99L9.78 6.44Z';
const SPARKLE_SMALL =
    'M18.67 2L18.42 2.66C18.1 3.53 17.94 3.97 17.62 4.29C17.3 4.6 16.87 4.77 16 5.09L15.33 5.33L16 5.58C16.87 5.9 17.3 6.06 17.62 6.38C17.94 6.7 18.1 7.13 18.42 8L18.67 8.67L18.91 8C19.23 7.13 19.4 6.7 19.71 6.38C20.03 6.06 20.47 5.9 21.34 5.58L22 5.33L21.34 5.09C20.47 4.77 20.03 4.6 19.71 4.29C19.4 3.97 19.23 3.53 18.91 2.66L18.67 2Z';

const SparklesIcon = forwardRef<SVGSVGElement, IconProps>(
    ({ size = 24, color = 'currentColor', variant = 'Linear', style, ...rest }, ref) => {
        const filled = variant === 'Bold' || variant === 'Bulk';
        return (
            <svg
                ref={ref}
                xmlns="http://www.w3.org/2000/svg"
                width={size}
                height={size}
                viewBox="0 0 24 24"
                fill={filled ? 'currentColor' : 'none'}
                stroke={filled ? 'none' : 'currentColor'}
                strokeWidth={1.5}
                strokeLinejoin="round"
                style={color !== 'currentColor' ? { color, ...style } : style}
                {...rest}
            >
                <path d={SPARKLE_LARGE} />
                <path d={SPARKLE_SMALL} opacity={variant === 'Bulk' ? 0.4 : undefined} />
            </svg>
        );
    },
);
SparklesIcon.displayName = 'Sparkles';

/**
 * The app's AI mark: a tall four-point star with a smaller companion — the
 * glyph people read as "AI". Replaces Iconsax's `MagicStar`, whose
 * wand-flicked star reads as a magic trick instead. Drawn on a 20-unit grid
 * and rescaled onto Iconsax's 24 box with a 1.5 stroke so it sits flush next
 * to Linear siblings; `variant="Bold"`/`"Bulk"` fill it (Bulk fades the small
 * star) to match the two-tone icons it shares badges and tiles with.
 */
export const Sparkles: IconComponent = SparklesIcon;
